import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import StructureLookupWidget from '../components/StructureLookupWidget';
import AssessorRunPanel from '../components/AssessorRunPanel';
import ProxyAuditPanel from '../components/ProxyAuditPanel';
import DemoBrandPanel from '../components/DemoBrandPanel';
import {
  CheckCircle, Warning, WarningCircle, MagnifyingGlass, Lightning,
  ClockCounterClockwise, Eye, FileDoc, PawPrint, BookOpen,
  Database, ShieldCheck,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Counter({ label, value, sub, color = 'text-[#111827]' }) {
  return (
    <div className="card-flat p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-[#4B5563] mt-1">{sub}</p>}
    </div>
  );
}

const STATUS_META = {
  acceptable:     { cls: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]', Icon: CheckCircle, label: 'OK' },
  needs_review:   { cls: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]', Icon: Warning, label: 'REVIEW' },
  not_acceptable: { cls: 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]', Icon: WarningCircle, label: 'BLOCKED' },
};

function ActivityRow({ a }) {
  const m = STATUS_META[a.overall_status] || STATUS_META.needs_review;
  const date = a.updated_at ? new Date(a.updated_at).toLocaleString() : '—';
  return (
    <Link to={`/assessment/${a.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0" data-testid={`activity-row-${a.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-7 h-7 rounded flex items-center justify-center border ${m.cls}`}>
          <m.Icon size={14} weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-[#111827] truncate">{a.product_name || 'Untitled'}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-gray-100 text-[#4B5563] rounded">{a.mode}</span>
            {a.signed && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#DEF7EC] text-[#03543F] border border-[#31C48D] rounded">SIGNED</span>}
            {a.flagged && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">FLAGGED</span>}
            {a.has_pet_assessment && <PawPrint size={12} className="text-[#4B5563]" weight="duotone" />}
          </div>
          <p className="text-[11px] text-[#4B5563] truncate">
            {a.client_company || a.client_name || a.client_email || '—'}
            {a.client_email && <> · <span className="font-mono">{a.client_email}</span></>}
          </p>
        </div>
      </div>
      <span className="text-[10px] font-mono tabular-nums text-[#4B5563] flex-shrink-0">{date}</span>
    </Link>
  );
}

/** Quick Hazard Test — calls /api/formulations/screen-preview without saving. */
function QuickHazardTest() {
  const [rows, setRows] = useState([
    { inci_name: 'Hydroquinone', concentration: 2 },
    { inci_name: 'Glycerin', concentration: 5 },
  ]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setRow = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows(rs => [...rs, { inci_name: '', concentration: 0 }]);
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const run = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const valid = rows.filter(r => r.inci_name.trim() && r.concentration);
      const r = await axios.post(`${API_URL}/api/formulations/screen-preview`,
        { ingredients: valid.map(x => ({ inci_name: x.inci_name.trim(), concentration: parseFloat(x.concentration) || 0 })) },
        { withCredentials: true });
      setData(r.data);
    } catch { setError('Hazard screen failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="card-flat p-5" data-testid="quick-hazard-panel">
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={20} className="text-[#111827]" weight="duotone" />
        <h3 className="text-sm font-semibold text-[#111827]">Quick Hazard Test</h3>
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#4B5563]">no save · live screen</span>
      </div>
      <div className="space-y-2 mb-3">
        {rows.map((r, i) => (
          <div key={`row-${i}`} className="flex gap-2">
            <Input value={r.inci_name} onChange={e => setRow(i, { inci_name: e.target.value })}
              placeholder="INCI name" className="h-9 text-sm flex-1" data-testid={`qh-inci-${i}`} />
            <Input type="number" step="0.01" value={r.concentration} onChange={e => setRow(i, { concentration: e.target.value })}
              placeholder="%" className="h-9 text-sm w-24" data-testid={`qh-conc-${i}`} />
            <Button variant="outline" onClick={() => removeRow(i)} className="h-9 rounded text-xs px-2">×</Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <Button variant="outline" onClick={addRow} className="rounded h-8 text-xs">+ Row</Button>
        <Button onClick={run} disabled={loading} className="btn-primary h-8 text-xs" data-testid="qh-run-btn">
          {loading ? 'Running…' : 'Screen'}
        </Button>
      </div>
      {error && <p className="text-xs text-[#9B1C1C]">{error}</p>}
      {data && (
        <div className="border border-gray-200 rounded p-3 text-xs" data-testid="qh-result">
          <p className="font-bold text-[#111827] mb-2">
            Overall: <span className={STATUS_META[data.overall_status]?.cls.split(' ')[1] || ''}>
              {data.overall_status.replace('_', ' ').toUpperCase()}
            </span> · {data.total_ingredients} ingredient(s)
          </p>
          <div className="flex flex-wrap gap-1 mb-2">
            {Object.entries(data.hazard_flags).filter(([, v]) => v > 0).map(([k, v]) => (
              <span key={k} className="text-[10px] font-bold px-1.5 py-0.5 bg-[#FEF3C7] border border-[#F3CC64] text-[#723B13] rounded">
                {k}: {v}
              </span>
            ))}
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {data.results.map(r => (
              <div key={r.ingredient_name} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded text-[11px]">
                <span className={`text-[9px] font-bold uppercase px-1 py-0.5 rounded ${STATUS_META[r.risk_level]?.cls || ''}`}>
                  {STATUS_META[r.risk_level]?.label || r.risk_level}
                </span>
                <span className="font-semibold text-[#111827]">{r.ingredient_name}</span>
                <span className="text-[#4B5563]">{r.concentration}%</span>
                <span className="text-[#4B5563] truncate">{r.interpretation}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Ingredient DB browser — search the curated 112-entry library (read-only). */
function IngredientDBBrowser() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ingredients?limit=500`, { withCredentials: true });
      setItems(r.data.ingredients || []); setTotal(r.data.total || 0);
    } catch (e) {
      // 401s here are common on mobile preview iframes where cookies don't propagate.
      // Swallow so the dev overlay doesn't surface an uncaught rejection.
      console.warn('[AssessorPage] ingredient DB load failed:', e?.response?.status || e?.message);
      setItems([]); setTotal(0);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = q
    ? items.filter(i => i.inci_name.toLowerCase().includes(q.toLowerCase()) || (i.cas_number || '').includes(q))
    : items;

  return (
    <div className="card-flat" data-testid="ingredient-db-panel">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2">
        <Database size={18} className="text-[#111827]" weight="duotone" />
        <h3 className="text-sm font-semibold text-[#111827]">Aᴳ Ingredient Database</h3>
        <span className="text-[10px] text-[#4B5563]">{total} curated entries</span>
        <div className="relative ml-auto w-64">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search INCI / CAS…" className="h-8 text-xs pl-8" data-testid="db-search" />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center text-xs text-[#4B5563]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-[#4B5563]">No matches</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left py-1.5 px-3 font-semibold text-[#4B5563]">INCI</th>
                <th className="text-left py-1.5 px-3 font-semibold text-[#4B5563]">CAS</th>
                <th className="text-left py-1.5 px-3 font-semibold text-[#4B5563]">Function</th>
                <th className="text-right py-1.5 px-3 font-semibold text-[#4B5563]">NOAEL</th>
                <th className="text-left py-1.5 px-3 font-semibold text-[#4B5563]">Sens.</th>
                <th className="text-left py-1.5 px-3 font-semibold text-[#4B5563]">Flags</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map(i => (
                <tr key={i.inci_name} className="border-t border-gray-100">
                  <td className="py-1.5 px-3 font-semibold text-[#111827]">{i.inci_name}</td>
                  <td className="py-1.5 px-3 font-mono text-[#4B5563]">{i.cas_number || '—'}</td>
                  <td className="py-1.5 px-3 text-[#4B5563]">{i.function_class || '—'}</td>
                  <td className="py-1.5 px-3 text-right tabular-nums">{i.noael ?? '—'}</td>
                  <td className="py-1.5 px-3 text-[#4B5563]">{i.skin_sensitization || '—'}</td>
                  <td className="py-1.5 px-3">
                    <div className="flex gap-1 flex-wrap">
                      {i.genotoxicity && <span className="text-[9px] font-bold px-1 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">GEN</span>}
                      {i.eu_annex_ii && <span className="text-[9px] font-bold px-1 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">EU-II</span>}
                      {i.eu_annex_iii && <span className="text-[9px] font-bold px-1 bg-[#FEF3C7] text-[#723B13] border border-[#F3CC64] rounded">EU-III</span>}
                      {i.ifra_restricted && <span className="text-[9px] font-bold px-1 bg-[#FEF3C7] text-[#723B13] border border-[#F3CC64] rounded">IFRA</span>}
                      {i.prop_65 && <span className="text-[9px] font-bold px-1 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">P65</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AssessorPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/assessor-snapshot`, { withCredentials: true });
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Assessor QA Console</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">internal · admin only</span>
          </div>
          <p className="text-sm text-[#4B5563]">
            Cross-tenant inspection of the platform. Distinct from the client portal — designed for verifying that screening, QSAR, read-across, SDS generation, pet auto-triggers, and admin sign-offs are flowing correctly.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/admin"><Button variant="outline" className="h-8 text-xs rounded" data-testid="assessor-to-admin">Admin Review →</Button></Link>
            <Link to="/assessor/noael-extraction"><Button className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="assessor-to-noael">NOAEL Extraction →</Button></Link>
            <Link to="/assessor/formula-db"><Button className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="assessor-to-formula-db">Formula DB →</Button></Link>
            <Link to="/assessor/hale-applications"><Button className="h-8 text-xs rounded bg-[#E63946] hover:bg-[#C9192C] text-white" data-testid="assessor-to-hale-apps">HALE Applications →</Button></Link>
            <Link to="/assessor/hale-invoices"><Button className="h-8 text-xs rounded bg-[#E63946] hover:bg-[#C9192C] text-white" data-testid="assessor-to-hale-invoices">HALE Invoices →</Button></Link>
            <Link to="/assessor/exposure"><Button className="h-8 text-xs rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white" data-testid="assessor-to-exposure">Exposure &amp; MoS Engine →</Button></Link>
            <Link to="/assessor/hazard-intelligence"><Button className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="assessor-to-hazard-intel">Hazard Intelligence →</Button></Link>
            <Link to="/assessor/portfolio"><Button className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="assessor-to-portfolio">Portfolio Triage →</Button></Link>
            <Link to="/assessor/hale"><Button className="h-8 text-xs rounded bg-[#E63946] hover:bg-[#C9192C] text-white" data-testid="assessor-to-hale">Hale Console →</Button></Link>
            <Link to="/assessor/hale-verified"><Button className="h-8 text-xs rounded bg-[#E63946] hover:bg-[#C9192C] text-white" data-testid="assessor-to-hale-verified">Hale Verified →</Button></Link>
            <Link to="/assessor/hale-command"><Button className="h-8 text-xs rounded bg-[#E63946] hover:bg-[#C9192C] text-white" data-testid="assessor-to-hale-command">Hale Command →</Button></Link>
            <Link to="/assessor/supplier-specs"><Button className="h-8 text-xs rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white" data-testid="assessor-to-supplier-specs">Supplier Specs →</Button></Link>
            <Link to="/assessor/marketplace"><Button className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="assessor-to-marketplace">Marketplace Admin →</Button></Link>
            <Link to="/dashboard"><Button variant="outline" className="h-8 text-xs rounded" data-testid="assessor-to-portal">Client Portal (as user) →</Button></Link>
          </div>
        </div>

        <DemoBrandPanel />

        {/* Counters */}
        {loading ? (
          <div className="card-flat p-6 text-sm text-[#4B5563]">Loading snapshot…</div>
        ) : !data ? (
          <div className="card-flat p-6 flex items-center justify-between" data-testid="snapshot-retry">
            <p className="text-sm text-[#4B5563]">Snapshot didn't load — your session may have expired.</p>
            <Button onClick={loadSnapshot} variant="outline" className="h-8 text-xs rounded">Retry</Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <Counter label="Brand users" value={data.counters.brand_users} sub={`${data.counters.total_users} total accounts`} />
              <Counter label="Formulations" value={data.counters.total_formulations} sub={`${data.counters.hazard_count} hazard · ${data.counters.full_count} full`} />
              <Counter label="Signed CPSRs" value={data.counters.signed_count} color="text-[#0E9F6E]" />
              <Counter label="Flagged" value={data.counters.flagged_count} color={data.counters.flagged_count > 0 ? 'text-[#9B1C1C]' : 'text-[#111827]'} />
              <Counter label="Pet screens" value={data.counters.pet_count} sub="auto-triggered" />
              <Counter label="Aᴳ ingredient DB" value={data.counters.ingredient_db_size} color="text-[#FF4E6A]" />
              <Counter label="Private library" value={data.counters.library_entries} sub="across all clients" />
            </div>

            {/* Activity feed */}
            <div className="card-flat mb-6" data-testid="activity-feed">
              <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                <ClockCounterClockwise size={18} className="text-[#111827]" weight="duotone" />
                <h2 className="text-sm font-semibold text-[#111827]">Recent activity (last 20 across all clients)</h2>
              </div>
              {data.activity.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#4B5563]">No activity yet.</div>
              ) : (
                <div>{data.activity.map(a => <ActivityRow key={a.id} a={a} />)}</div>
              )}
            </div>
          </>
        )}

        {/* QA tools */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <AssessorRunPanel />
          <QuickHazardTest />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <StructureLookupWidget compact />
          <ProxyAuditPanel />
        </div>

        {/* Ingredient DB browser */}
        <IngredientDBBrowser />
      </main>
    </div>
  );
}
